import {
  Box,
  Button,
  Heading,
  Stack,
  useToast,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Text,
  Divider,
  Spinner,
  Flex,
  Select,
  IconButton,
  Checkbox,
  Textarea,
  HStack,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  FormControl,
  FormErrorMessage,
  ButtonGroup,
  InputGroup,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { GoSearch } from "react-icons/go";
import { Formik, FieldArray, Form, FormikErrors } from "formik";
import * as Yup from "yup";
import { useQuery, useMutation } from "react-query";
import { getSubjects } from "API/subjectApi";
import { getPrograms } from "API/programApi";
import { filterQuestion } from "API/questionApi";
import { createQuestionPaper } from "API/questionpaperAPI";
import { useState, useMemo, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";

type SectionSelectionType = "ALL" | "ANY" | "AT_LEAST" | "AT_MOST";
type SectionSelectionPolicy = "FIRST_N" | "BEST_N";

interface SectionQuestionEntry {
  questionId: string;
  marks: number;
  requiresStudentUpload?: boolean;
}

interface Section {
  name: string;
  selectionType?: SectionSelectionType;
  optionalCount?: number;
  selectionPolicy?: SectionSelectionPolicy;
  instructions?: string;
  shuffleQuestions?: boolean;
  questions: SectionQuestionEntry[];
}

interface Question {
  _id: string;
  question: string;
  type?: string;
  questionImg?: string;
  positiveMarks?: number;
  requiresUpload?: boolean;
}

interface FormValues {
  title: string;
  subject: string;
  programId: string;
  duration: number;
  totalMarks: number;
  sections: Section[];
}

interface PaginationState {
  page: number;
  size: number;
  searchText: string;
  questionType: string;
  level: string;
}

const computeSectionIntendedMax = (section: Section) => {
  const marksList = (section.questions || [])
    .map((q) => Number(q.marks || 0))
    .sort((a, b) => b - a);
  const selType = (section.selectionType || "ALL").toUpperCase();
  const n = Math.max(0, Number(section.optionalCount || 0));
  let sum = 0;
  if (selType === "ALL") {
    sum = marksList.reduce((a, b) => a + b, 0);
  } else if (selType === "ANY" || selType === "AT_MOST") {
    sum = marksList.slice(0, n).reduce((a, b) => a + b, 0);
  } else if (selType === "AT_LEAST") {
    sum = marksList.reduce((a, b) => a + b, 0);
  }
  return sum;
};

const sectionSchema = Yup.object({
  name: Yup.string().required("Section name is required"),
  selectionType: Yup.string().oneOf(["ALL", "ANY", "AT_LEAST", "AT_MOST"]).optional(),
  optionalCount: Yup.number().when("selectionType", {
    is: (v: string) => v && v !== "ALL",
    then: (schema) => schema.min(1, "Must be at least 1").required("Number is required"),
    otherwise: (schema) => schema.optional(),
  }),
  selectionPolicy: Yup.string().oneOf(["FIRST_N", "BEST_N"]).optional(),
  instructions: Yup.string().max(500).optional(),
  shuffleQuestions: Yup.boolean().optional(),
  questions: Yup.array()
    .of(
      Yup.object({
        questionId: Yup.string().required("Question must be selected"),
        marks: Yup.number().min(1, "Enter marks").required("Marks are required"),
        requiresStudentUpload: Yup.boolean().optional(),
      })
    )
    .min(1, "Select at least one question"),
});

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  subject: Yup.string().required("Subject is required"),
  programId: Yup.string().required("Program is required"),
  duration: Yup.number().required("Duration is required").positive("Duration must be positive"),
  totalMarks: Yup.number().required("Total marks is required").positive("Total marks must be positive"),
  sections: Yup.array().of(sectionSchema).min(1, "At least one section is required"),
});

const CreateQuestionPaper = () => {
  const toast = useToast();
  const history = useHistory();
  const [inlineError, setInlineError] = useState<string>("");
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [summaryItems, setSummaryItems] = useState<string[]>([]);
  const summaryRef = useRef<HTMLDivElement | null>(null);

  // Enhanced Pagination State (Removed requiresUpload)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    size: 10,
    searchText: "",
    questionType: "",
    level: "",
  });

  const { data: subjects = [] } = useQuery("subjects", getSubjects);
  const { data: programs = [] } = useQuery("programs", getPrograms);

  const { mutateAsync, isLoading: submitting } = useMutation(createQuestionPaper, {
    onSuccess: () => {
      setInlineError("");
      setShowSummary(false);
      setSummaryItems([]);
      toast({
        title: "Success",
        description: "Question paper created successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      history.push("/admin/dashboard/question-papers");
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || "Failed to create question paper.";
      setInlineError(msg);
      toast({
        title: "Error",
        description: msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  function validate(values: FormValues) {
    const errors: Partial<FormikErrors<FormValues>> = {};
    const sectionErrorsArr: any[] = [];

    values.sections.forEach((section, i) => {
      const selType = section.selectionType || "ALL";
      const qLen = section.questions.length;
      const sErr: any = {};
      if (selType !== "ALL") {
        const n = Number(section.optionalCount || 0);
        if (n > qLen) {
          sErr.optionalCount = `Cannot be more than number of questions (${qLen})`;
        } else if (n < 1) {
          sErr.optionalCount = `Must be at least 1`;
        }
      }
      sectionErrorsArr[i] = sErr;
    });
    if (sectionErrorsArr.some((e) => Object.keys(e).length > 0)) {
      (errors as any).sections = sectionErrorsArr;
    }

    // Computed marks validations
    const perSectionIntended = values.sections.map((s) => computeSectionIntendedMax(s));
    const totalIntended = perSectionIntended.reduce((a, b) => a + b, 0);
    const msgs: string[] = [];

    values.sections.forEach((s, idx) => {
      const intended = perSectionIntended[idx];
      if ((s.selectionType || "ALL") === "ANY") {
        const n = Number(s.optionalCount || 0);
        const available = s.questions.length;
        if (n > available) {
          msgs.push(`Section ${idx + 1}: N cannot exceed available questions (${available}).`);
        }
      }
      if (intended <= 0) {
        msgs.push(`Section ${idx + 1}: No marks selected. Add questions or marks.`);
      }
    });

    if (Number(values.totalMarks) > 0 && totalIntended !== Number(values.totalMarks)) {
      msgs.push(
        `Total marks (${values.totalMarks}) should equal sum of section intended marks (${totalIntended}).`
      );
    }

    if (msgs.length > 0) {
      (errors as any).__marksGuidance = msgs;
    }

    return errors;
  }

  const buildErrorSummary = (errors: any, values: FormValues) => {
    const items: string[] = [];
    if (errors?.title) items.push("Paper Title is required");
    if (errors?.subject) items.push("Subject is required");
    if (errors?.programId) items.push("Program is required");
    if (errors?.duration) items.push(typeof errors.duration === "string" ? errors.duration : "Invalid Duration");
    if (errors?.totalMarks) items.push(typeof errors.totalMarks === "string" ? errors.totalMarks : "Invalid Total Marks");

    if (Array.isArray(errors?.sections)) {
      errors.sections.forEach((secErr: any, i: number) => {
        const secName = values.sections[i]?.name || `Section ${i + 1}`;
        if (secErr?.name) items.push(`${secName}: ${secErr.name}`);
        if (secErr?.optionalCount) items.push(`${secName}: ${secErr.optionalCount}`);
        if (secErr?.questions) {
          if (typeof secErr.questions === "string") {
            items.push(`${secName}: ${secErr.questions}`);
          }
        }
      });
    } else if (errors?.sections) {
      items.push(typeof errors.sections === "string" ? errors.sections : "Sections need attention");
    }

    if (Array.isArray(errors?.__marksGuidance)) {
      items.push(...errors.__marksGuidance);
    }

    return Array.from(new Set(items));
  };

  // Enhanced Pagination Handlers
  const updatePagination = (updates: Partial<PaginationState>) => {
    setPagination(prev => ({ ...prev, ...updates, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 1 }));
  };

  useEffect(() => {
    if (showSummary && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showSummary]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Create Question Paper</Heading>
        <Button colorScheme="blue" size="sm" onClick={() => history.push("/admin/dashboard/question-papers")}>
          View Question Papers
        </Button>
      </Flex>

      <Formik<FormValues>
        enableReinitialize
        initialValues={{
          title: "",
          subject: "",
          programId: "",
          duration: 60,
          totalMarks: 100,
          sections: [
            {
              name: "",
              selectionType: "ALL",
              selectionPolicy: "FIRST_N",
              instructions: "",
              shuffleQuestions: false,
              questions: [],
            },
          ],
        }}
        validationSchema={validationSchema}
        validate={validate}
        validateOnChange
        validateOnBlur
        onSubmit={async (values, actions) => {
          try {
            await mutateAsync(values);
            actions.resetForm();
          } catch {}
        }}
      >
        {({ values, setFieldValue, errors, touched, validateForm, submitForm }) => {
          // Enhanced Query with Pagination and Filters
          const { data: questionsData, isLoading: questionLoading } = useQuery(
            [
              "filteredQuestions", 
              values.subject, 
              pagination.page, 
              pagination.size, 
              pagination.searchText, 
              pagination.questionType, 
              pagination.level
            ],
            () => {
              if (!values.subject) return { questions: [], count: 0, totalPage: 0 };
              
              // Build filter parameters using existing API structure
              const filterParams: any = {
                subject: values.subject,
                page: pagination.page,
                size: pagination.size,
              };

              // Add optional filters
              if (pagination.searchText?.trim()) {
                filterParams.searchText = pagination.searchText.trim();
              }
              if (pagination.questionType?.trim()) {
                filterParams.questionType = pagination.questionType.trim();
              }
              if (pagination.level?.trim()) {
                filterParams.level = pagination.level.trim();
              }

              return filterQuestion(filterParams);
            },
            { 
              enabled: !!values.subject,
              keepPreviousData: true 
            }
          );

          const questions: Question[] = questionsData?.questions || [];
          const totalQuestions = questionsData?.count || 0;
          const totalPages = questionsData?.totalPage || Math.ceil(totalQuestions / pagination.size);

          const paperMarksSelected = values.sections.reduce(
            (acc, section) => acc + section.questions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0),
            0
          );
          const intendedFromSections = values.sections.reduce(
            (acc, sec) => acc + computeSectionIntendedMax(sec),
            0
          );

          const getQuestionDefaultMarks = (qId: string) => {
            const q = questions.find((x) => x._id === qId);
            const m = Number(q?.positiveMarks); // Using positiveMarks
            return Number.isFinite(m) && m > 0 ? m : 1;
          };

          const handleSubmitWithSummary = async () => {
            const formErrors = await validateForm();
            const items = buildErrorSummary(formErrors, values);
            if (items.length > 0) {
              setShowSummary(true);
              setSummaryItems(items);
              return;
            }
            setShowSummary(false);
            setSummaryItems([]);
            await submitForm();
          };

          return (
            <Form>
              <Stack spacing={5}>
                {showSummary && summaryItems.length > 0 && (
                  <Alert status="error" variant="left-accent" borderRadius="md" ref={summaryRef}>
                    <AlertIcon />
                    <Box flex="1">
                      <AlertTitle>Fix the following before creating the paper</AlertTitle>
                      <AlertDescription>
                        <VStack align="start" spacing={1} mt={2}>
                          {summaryItems.map((item, i) => (
                            <Text key={i} fontSize="sm">
                              • {item}
                            </Text>
                          ))}
                        </VStack>
                      </AlertDescription>
                    </Box>
                    <CloseButton
                      position="relative"
                      onClick={() => {
                        setShowSummary(false);
                        setSummaryItems([]);
                      }}
                    />
                  </Alert>
                )}

                <FormControl isRequired isInvalid={!!touched.title && !!errors.title}>
                  <FormLabel>Paper Title</FormLabel>
                  <Input
                    placeholder="Enter paper title"
                    value={values.title}
                    onChange={(e) => setFieldValue("title", e.target.value)}
                  />
                  <FormErrorMessage>{errors.title as any}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!touched.subject && !!errors.subject}>
                  <FormLabel>Subject</FormLabel>
                  <Select
                    placeholder="Select Subject"
                    value={values.subject}
                    onChange={(e) => {
                      setFieldValue("subject", e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    {subjects.map((sub: any) => (
                      <option key={sub._id} value={sub.name}>
                        {sub.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.subject as any}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!touched.programId && !!errors.programId}>
                  <FormLabel>Program</FormLabel>
                  <Select
                    placeholder="Select Program"
                    value={values.programId}
                    onChange={(e) => setFieldValue("programId", e.target.value)}
                  >
                    {programs.map((program: any) => (
                      <option key={program._id} value={program._id}>
                        {program.name || program._id}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.programId as any}</FormErrorMessage>
                </FormControl>

                <Flex gap={6}>
                  <FormControl flex="1 1 50%" isRequired isInvalid={!!touched.duration && !!errors.duration}>
                    <FormLabel>Duration (minutes)</FormLabel>
                    <NumberInput
                      value={values.duration}
                      onChange={(val) => setFieldValue("duration", Number(val || 0))}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <FormErrorMessage>{errors.duration as any}</FormErrorMessage>
                  </FormControl>

                  <FormControl flex="1 1 50%" isRequired isInvalid={!!touched.totalMarks && !!errors.totalMarks}>
                    <FormLabel>Total Marks</FormLabel>
                    <NumberInput
                      value={values.totalMarks}
                      onChange={(val) => setFieldValue("totalMarks", Number(val || 0))}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <FormErrorMessage>{errors.totalMarks as any}</FormErrorMessage>
                  </FormControl>
                </Flex>

                <Box>
                  <Text fontWeight="semibold" color="gray.600">
                    Selected marks: {paperMarksSelected} / {values.totalMarks}
                  </Text>
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme="purple" variant="subtle">
                      Max possible (guide): {intendedFromSections}
                    </Badge>
                    {intendedFromSections !== values.totalMarks && (
                      <Text fontSize="xs" color="gray.500">
                        Tip: Align total marks with section intended sum.
                      </Text>
                    )}
                  </HStack>
                </Box>

                <FieldArray name="sections">
                  {(arrayHelpers) => (
                    <Stack spacing={5}>
                      {values.sections.map((section, idx) => {
                        const selectedInOtherSections = new Set<string>(
                          values.sections
                            .filter((_s, i) => i !== idx)
                            .flatMap((s) => s.questions.map((q) => q.questionId))
                        );
                        const availableQuestions = (questions || []).filter(
                          (q) => !selectedInOtherSections.has(q._id)
                        );
                        const sectionQuestionIds = section.questions.map((q) => q.questionId);

                        const sectionErr =
                          Array.isArray((errors as any).sections) &&
                          (errors as any).sections[idx] &&
                          typeof (errors as any).sections[idx] === "object"
                            ? ((errors as any).sections[idx] as any)
                            : {};

                        const sectionMarks = section.questions.reduce(
                          (acc, q) => acc + (Number(q.marks) || 0),
                          0
                        );
                        const intendedMax = computeSectionIntendedMax(section);

                        return (
                          <Box
                            key={idx}
                            p={3}
                            border="1px"
                            borderColor="gray.300"
                            borderRadius="md"
                            bg="gray.50"
                          >
                            <Flex alignItems="center" justifyContent="space-between" mb={2}>
                              <Heading size="sm">Section {idx + 1}</Heading>
                              <IconButton
                                aria-label="Delete Section"
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                disabled={values.sections.length === 1}
                                onClick={() => arrayHelpers.remove(idx)}
                              />
                            </Flex>
                            <Divider my={2} />

                            <Stack spacing={3}>
                              <FormControl
                                isRequired
                                isInvalid={!!(errors as any)?.sections?.[idx]?.name}
                              >
                                <FormLabel>Section Name</FormLabel>
                                <Input
                                  placeholder="e.g., Explain in detail"
                                  value={section.name}
                                  onChange={(e) =>
                                    setFieldValue(`sections[${idx}].name`, e.target.value)
                                  }
                                />
                                <FormErrorMessage>
                                  {(errors as any)?.sections?.[idx]?.name as any}
                                </FormErrorMessage>
                              </FormControl>

                              <Flex gap={3} wrap="wrap">
                                <Box minW="240px" flex="1">
                                  <FormLabel>Answer rule</FormLabel>
                                  <Select
                                    value={section.selectionType || "ALL"}
                                    onChange={(e) =>
                                      setFieldValue(`sections[${idx}].selectionType`, e.target.value)
                                    }
                                  >
                                    <option value="ALL">All questions</option>
                                    <option value="ANY">Exactly N questions</option>
                                  </Select>
                                  <Text fontSize="xs" color="gray.500" mt={1}>
                                    Choose how many must be answered.
                                  </Text>
                                  {sectionErr?.optionalCount && (
                                    <Text color="red.500" fontSize="sm">
                                      {sectionErr.optionalCount}
                                    </Text>
                                  )}
                                </Box>

                                {section.selectionType && section.selectionType !== "ALL" && (
                                  <Box minW="120px" flex="1">
                                    <FormLabel>Number (N)</FormLabel>
                                    <NumberInput
                                      min={1}
                                      value={section.optionalCount || 1}
                                      onChange={(_s, val) =>
                                        setFieldValue(
                                          `sections[${idx}].optionalCount`,
                                          val || 1
                                        )
                                      }
                                    >
                                      <NumberInputField />
                                    </NumberInput>
                                  </Box>
                                )}

                                <Box minW="240px" flex="1">
                                  <FormLabel>If more than allowed are answered</FormLabel>
                                  <Select
                                    value={section.selectionPolicy || "FIRST_N"}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `sections[${idx}].selectionPolicy`,
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="FIRST_N">Score first N answers</option>
                                    <option value="BEST_N">Score best N answers</option>
                                  </Select>
                                </Box>

                                <Box minW="140px" display="flex" alignItems="flex-end">
                                  <Checkbox
                                    isChecked={!!section.shuffleQuestions}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `sections[${idx}].shuffleQuestions`,
                                        e.target.checked
                                      )
                                    }
                                  >
                                    Shuffle questions
                                  </Checkbox>
                                </Box>
                              </Flex>

                              <Box>
                                <FormLabel>Student instructions (optional)</FormLabel>
                                <Textarea
                                  placeholder="e.g., Answer any 4 out of the following 5 questions."
                                  value={section.instructions || ""}
                                  onChange={(e) =>
                                    setFieldValue(`sections[${idx}].instructions`, e.target.value)
                                  }
                                  rows={2}
                                />
                              </Box>

                              <HStack spacing={3}>
                                <Badge colorScheme="purple" variant="subtle">
                                  Section marks (selected): {sectionMarks}
                                </Badge>
                                <Badge colorScheme="blue" variant="subtle">
                                  Max possible (guide): {intendedMax}
                                </Badge>
                                {intendedMax <= 0 && (
                                  <Text fontSize="xs" color="red.500">
                                    Add questions and marks for this section
                                  </Text>
                                )}
                              </HStack>

                              {/* Search and Filter Questions - Removed requires upload filter */}
                              <Box>
                                <FormLabel>Search and Filter Questions</FormLabel>
                                <Stack direction={["column", "row"]} spacing={3} mb={3}>
                                  <Box flex="2">
                                    <InputGroup size="sm">
                                      <Input
                                        placeholder="Search questions..."
                                        value={pagination.searchText}
                                        onChange={(e) => updatePagination({ searchText: e.target.value })}
                                        size="sm"
                                      />
                                      <IconButton
                                        aria-label="search"
                                        size="sm"
                                        ml={2}
                                        colorScheme="teal"
                                        icon={<GoSearch />}
                                      />
                                    </InputGroup>
                                  </Box>
                                  <Box flex="1">
                                    <Select
                                      placeholder="Question Type"
                                      size="sm"
                                      value={pagination.questionType}
                                      onChange={(e) => updatePagination({ questionType: e.target.value })}
                                    >
                                      
                                      <option value="MCQS">MCQ Single</option>
                                      <option value="MCQM">MCQ Multiple</option>
                                      <option value="TF">True/False</option>
                                      <option value="SHORT">Short Answer</option>
                                      <option value="LA">Long Answer</option>
                                      <option value="DIAGRAM">Diagram</option>
                                    </Select>
                                  </Box>
                                  <Box flex="1">
                                    <Select
                                      placeholder="Level"
                                      size="sm"
                                      value={pagination.level}
                                      onChange={(e) => updatePagination({ level: e.target.value })}
                                    >
                                      
                                      <option value="easy">Easy</option>
                                      <option value="normal">Normal</option>
                                      <option value="difficult">Difficult</option>
                                    </Select>
                                  </Box>
                                </Stack>
                              </Box>

                              <Box>
                                <FormLabel>Questions in this section</FormLabel>
                                {questionLoading ? (
                                  <Flex justify="center" py={4}>
                                    <Spinner />
                                  </Flex>
                                ) : (
                                  <>
                                    {/* Removed Default Marks column */}
                                    <Table variant="simple" size="sm" mt={2}>
                                      <Thead>
                                        <Tr>
                                          <Th>Select</Th>
                                          <Th>Question</Th>
                                          <Th>Type</Th>
                                          <Th>Marks</Th>
                                          <Th>Requires Upload</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {(() => {
                                          if (availableQuestions.length === 0) {
                                            return (
                                              <Tr>
                                                <Td colSpan={5}>
                                                  <Text color="gray.500" textAlign="center">
                                                    No questions available for chosen subject/type/filters
                                                  </Text>
                                                </Td>
                                              </Tr>
                                            );
                                          }
                                          return availableQuestions.map((q) => {
                                            const checked = sectionQuestionIds.includes(q._id);
                                            const secQIdx = section.questions.findIndex(
                                              (qq) => qq.questionId === q._id
                                            );
                                            const qEntry = section.questions[secQIdx];
                                            const qMarks = qEntry?.marks || 1;
                                            const requiresUpload = !!qEntry?.requiresStudentUpload;
                                            return (
                                              <Tr key={q._id}>
                                                <Td>
                                                  <Checkbox
                                                    isChecked={checked}
                                                    onChange={(e) => {
                                                      if (e.target.checked) {
                                                        const defaultMarks = getQuestionDefaultMarks(q._id);
                                                        setFieldValue(
                                                          `sections[${idx}].questions`,
                                                          [
                                                            ...section.questions,
                                                            {
                                                              questionId: q._id,
                                                              marks: defaultMarks,
                                                              requiresStudentUpload: q.requiresUpload || false,
                                                            },
                                                          ]
                                                        );
                                                      } else {
                                                        setFieldValue(
                                                          `sections[${idx}].questions`,
                                                          section.questions.filter(
                                                            (qq) => qq.questionId !== q._id
                                                          )
                                                        );
                                                      }
                                                    }}
                                                  />
                                                </Td>
                                                <Td>
                                                  <Text noOfLines={2}>{q.question}</Text>
                                                </Td>
                                                <Td>
                                                  <Text color="gray.500" fontSize="xs">
                                                    {(q.type || "Unknown").toUpperCase()}
                                                  </Text>
                                                </Td>
                                                {/* Removed Default Marks column content */}
                                                <Td>
                                                  {checked && (
                                                    <NumberInput
                                                      min={1}
                                                      step={1}
                                                      value={qMarks}
                                                      onChange={(_str, val) => {
                                                        const toSet = section.questions.map((qq) =>
                                                          qq.questionId === q._id
                                                            ? { ...qq, marks: val || 1 }
                                                            : qq
                                                        );
                                                        setFieldValue(`sections[${idx}].questions`, toSet);
                                                      }}
                                                      size="sm"
                                                      width="70px"
                                                    >
                                                      <NumberInputField />
                                                    </NumberInput>
                                                  )}
                                                </Td>
                                                <Td>
                                                  {checked && (
                                                    <Checkbox
                                                      isChecked={requiresUpload}
                                                      onChange={(e) => {
                                                        const toSet = section.questions.map((qq) =>
                                                          qq.questionId === q._id
                                                            ? {
                                                                ...qq,
                                                                requiresStudentUpload: e.target.checked,
                                                              }
                                                            : qq
                                                        );
                                                        setFieldValue(`sections[${idx}].questions`, toSet);
                                                      }}
                                                    >
                                                      Requires Upload
                                                    </Checkbox>
                                                  )}
                                                </Td>
                                              </Tr>
                                            );
                                          });
                                        })()}
                                      </Tbody>
                                    </Table>

                                    {/* Enhanced Pagination Controls */}
                                    <Stack
                                      direction={["column", "row"]}
                                      spacing={4}
                                      align="center"
                                      justify="space-between"
                                      mt={4}
                                      p={3}
                                      bg="gray.50"
                                      borderRadius="md"
                                    >
                                      <Box>
                                        <Select
                                          size="sm"
                                          value={pagination.size}
                                          onChange={(e) => handleSizeChange(Number(e.target.value))}
                                          maxW="120px"
                                        >
                                          <option value={5}>5 per page</option>
                                          <option value={10}>10 per page</option>
                                          <option value={20}>20 per page</option>
                                          <option value={50}>50 per page</option>
                                        </Select>
                                      </Box>

                                      <Box>
                                        <Text fontSize="sm" color="gray.600">
                                          Showing {(pagination.page - 1) * pagination.size + 1} to{" "}
                                          {Math.min(pagination.page * pagination.size, totalQuestions)} of{" "}
                                          {totalQuestions} questions
                                        </Text>
                                      </Box>

                                      <Box>
                                        <ButtonGroup size="sm" isAttached variant="outline">
                                          <Button
                                            isDisabled={pagination.page === 1}
                                            onClick={() => handlePageChange(pagination.page - 1)}
                                          >
                                            Previous
                                          </Button>
                                          <Button
                                            isDisabled={pagination.page === totalPages || totalPages === 0}
                                            onClick={() => handlePageChange(pagination.page + 1)}
                                          >
                                            Next
                                          </Button>
                                        </ButtonGroup>
                                      </Box>
                                    </Stack>
                                  </>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}

                      <Button
                        leftIcon={<AddIcon />}
                        onClick={() =>
                          arrayHelpers.push({
                            name: "",
                            selectionType: "ALL",
                            selectionPolicy: "FIRST_N",
                            instructions: "",
                            shuffleQuestions: false,
                            questions: [],
                          })
                        }
                        colorScheme="blue"
                        variant="outline"
                        isDisabled={values.sections.length >= 20}
                      >
                        Add Section
                      </Button>
                    </Stack>
                  )}
                </FieldArray>

                {inlineError && (
                  <Alert status="error" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">{inlineError}</Text>
                  </Alert>
                )}

                <Divider />

                <Button
                  type="button"
                  colorScheme="teal"
                  isLoading={submitting}
                  width="full"
                  onClick={handleSubmitWithSummary}
                >
                  Create Question Paper
                </Button>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateQuestionPaper;
